import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';

/**
 * Create a new submission detail
 * @param {string} submissionId - The submission ID for cache invalidation
 */
export const useCreateSubmissionDetail = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['submissionDetail', submissionId, 'create'],
    mutationFn: async newSubmissionDetail => {
      const response = await axios.post(`${HOST}/api/certification-tool/submission-details`, newSubmissionDetail);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate submission query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};

/**
 * Update a submission detail
 * @param {string} submissionId - The submission ID for cache invalidation
 */
export const useUpdateSubmissionDetail = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['submissionDetail', submissionId, 'update'],
    mutationFn: async ({ submissionDetailId, patchDocument }) => {
      const response = await axios.patch(
        `${HOST}/api/certification-tool/submission-details/${submissionDetailId}`,
        patchDocument,
        {
          headers: {
            'Content-Type': 'application/json-patch+json',
          },
        },
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate submission query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};

/**
 * Delete a submission detail
 * @param {string} submissionId - The submission ID for cache invalidation
 */
export const useDeleteSubmissionDetail = submissionId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['submissionDetail', submissionId, 'delete'],
    mutationFn: async submissionDetailId => {
      await axios.delete(`${HOST}/api/certification-tool/submission-details/${submissionDetailId}`);
      return submissionDetailId;
    },
    onSuccess: () => {
      // Invalidate submission query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['submissions'] });
    },
  });
};
